import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, CheckCircle2, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authApi } from '../services/api';

/**
 * Reset password — step 2. Reads the `?token=` query param, validates a
 * new password locally (≥8 chars, ≥1 letter, ≥1 number), then posts to
 * /api/auth/reset-password. On success we drop the user back at /login
 * so they enter their new credentials cleanly.
 */
const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('This link is missing a reset token. Please request a new one.');
    }
  }, [token]);

  const meets = {
    length: password.length >= 8,
    letter: /[A-Za-z]/.test(password),
    number: /\d/.test(password),
    match: confirm.length > 0 && password === confirm,
  };
  const allOk = meets.length && meets.letter && meets.number && meets.match;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) return;
    if (!allOk) {
      toast.error('Please choose a password that meets all the requirements.');
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword(token, password);
      setDone(true);
      toast.success('Password reset! You can now sign in.');
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Reset failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-background min-h-screen flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md relative z-10">
        <Card className="border-border bg-card/95 backdrop-blur-sm">
          <CardContent className="pt-8 pb-6">
            {!done ? (
              <>
                <h1 className="font-heading text-2xl font-bold mb-2">Reset your password</h1>
                <p className="text-sm text-muted-foreground mb-6">
                  Choose a new password for your account.
                </p>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="rp-new" className="uppercase text-xs tracking-wider">New password</Label>
                    <div className="relative">
                      <Input
                        id="rp-new"
                        type={show ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="input-industrial pr-12"
                        required
                        autoFocus
                        data-testid="reset-new-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShow((s) => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        tabIndex={-1}
                      >
                        {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="rp-confirm" className="uppercase text-xs tracking-wider">Confirm password</Label>
                    <Input
                      id="rp-confirm"
                      type={show ? 'text' : 'password'}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="input-industrial"
                      required
                      data-testid="reset-confirm-input"
                    />
                  </div>

                  <ul className="space-y-1 text-xs text-muted-foreground" data-testid="reset-rules">
                    <Rule met={meets.length}>At least 8 characters</Rule>
                    <Rule met={meets.letter}>At least one letter</Rule>
                    <Rule met={meets.number}>At least one number</Rule>
                    <Rule met={meets.match}>Both passwords match</Rule>
                  </ul>

                  <Button
                    type="submit"
                    className="w-full h-12 btn-industrial"
                    disabled={loading || !allOk || !token}
                    data-testid="reset-submit-btn"
                  >
                    {loading && <Loader2 className="w-5 h-5 animate-spin mr-2" />}
                    {loading ? 'Resetting...' : 'Reset password'}
                  </Button>
                </form>
              </>
            ) : (
              <div className="text-center py-4" data-testid="reset-success">
                <div className="w-12 h-12 rounded-full bg-green-500/10 mx-auto mb-4 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-400" />
                </div>
                <h2 className="font-heading text-xl font-bold mb-2">Password reset</h2>
                <p className="text-sm text-muted-foreground">Redirecting you to sign in...</p>
              </div>
            )}
            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-3 h-3" /> Back to login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const Rule = ({ met, children }) => (
  <li className={`flex items-center gap-2 ${met ? 'text-green-400' : 'text-muted-foreground'}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${met ? 'bg-green-400' : 'bg-muted-foreground/40'}`} />
    {children}
  </li>
);

export default ResetPasswordPage;
